import { createIdempotencyKey } from "@lafayette/api-client";
import type { StaffPaymentQuoteViewResponse } from "@lafayette/schemas";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { deepLinks } from "../../navigation/deep-links";
import { useAreaHref } from "../../navigation/use-offering";
import { hintClass, linkClass } from "../../requests/components/parts";
import { Amount } from "../../ui/amount";
import { Button } from "../../ui/button";
import { Dialog } from "../../ui/dialog";
import {
  CheckboxField,
  focusFirstInvalid,
  SelectField,
  TextAreaField,
  TextField,
} from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { describePaymentError, type PaymentFailure } from "../errors";
import { formatMinor, parseRupeesToMinor } from "../format";
import { quoteValidityOptions, supplierEvidenceOptions } from "../labels";
import { usePaymentsRuntime } from "../runtime";
import { PaymentFailureNotice } from "./parts";

// The quote dialogs of the request page (CHT-757): record a quote, replace one, and issue its payment request.
//   - The one amount a person types here is the SUPPLIER's own quote, in rupees, read from the text into integer paise. A payment request
//     has no amount field anywhere: issuing one names the quote and nothing else, and the server copies the amount from the approved quote.
//   - The customer terms are the words the customer reads (staff only see the supplier evidence reference).
//   - The validity is a number of days from the server clock, so no browser time zone is ever an input.
//   - ONE Idempotency-Key lives as long as a dialog. After a send that may have run, the fields are LOCKED and "Try again" sends the same
//     command with the same key. Escape and Cancel do nothing while a command is being sent.
//   - The COMPLETE body of the first send, `validUntil` included, is kept for every retry of the dialog. The validity is computed from the
//     clock once: a retry after a lost answer must not compute a later instant, or the server (same key, other body) refuses it.

const TEXT_MAX = 200;
const TERMS_MAX = 500;
const REASON_MAX = 500;

type Failure = PaymentFailure | null;

/** The shared shell: the key, the pending guard and the retry rule. */
function useDialogRun() {
  const [failure, setFailure] = useState<Failure>(null);
  const [inFlight, setInFlight] = useState(false);
  const [locked, setLocked] = useState(false);
  const sendingRef = useRef(false);
  const keyRef = useRef<string | null>(null);
  keyRef.current ??= createIdempotencyKey();
  const bodyRef = useRef<unknown>(null);
  const terminal = failure !== null && !failure.retryable;

  /** `build` runs once per dialog, on the first send; every retry sends that same body with the same key. */
  async function run<B, T>(
    build: () => B,
    work: (key: string, body: B) => Promise<T>,
    done: (value: T) => void,
  ) {
    if (sendingRef.current || terminal) return;
    sendingRef.current = true;
    setInFlight(true);
    setLocked(true);
    setFailure(null);
    try {
      bodyRef.current ??= build();
      done(await work(keyRef.current as string, bodyRef.current as B));
    } catch (error) {
      setFailure(describePaymentError(error, "quote"));
    } finally {
      sendingRef.current = false;
      setInFlight(false);
    }
  }
  const guardedClose = (close: () => void) => () => {
    if (!sendingRef.current) close();
  };
  return { failure, inFlight, locked, terminal, run, guardedClose };
}

type QuoteFieldsState = Readonly<{
  amount: string;
  description: string;
  terms: string;
  evidenceKind: string;
  evidenceReference: string;
  validityDays: string;
}>;
const EMPTY: QuoteFieldsState = {
  amount: "",
  description: "",
  terms: "",
  evidenceKind: "",
  evidenceReference: "",
  validityDays: "7",
};

type QuoteErrors = Partial<Record<keyof QuoteFieldsState, string>>;

function validateFields(fields: QuoteFieldsState): QuoteErrors {
  const errors: { -readonly [K in keyof QuoteErrors]: string } = {};
  if (parseRupeesToMinor(fields.amount) === null) {
    errors.amount = "Type the supplier's amount in rupees, above zero, with at most two decimals.";
  }
  const description = fields.description.trim();
  if (description === "") errors.description = "Write one line the customer sees.";
  else if (description.length > TEXT_MAX)
    errors.description = `Use at most ${TEXT_MAX} characters.`;
  const terms = fields.terms.trim();
  if (terms === "") errors.terms = "Write what the price covers and its conditions.";
  else if (terms.length > TERMS_MAX) errors.terms = `Use at most ${TERMS_MAX} characters.`;
  else if (
    [...terms].some((char) => {
      const code = char.charCodeAt(0);
      return code < 0x20 || (code >= 0x7f && code <= 0x9f);
    })
  ) {
    errors.terms = "Write the terms on one line, with no line breaks.";
  }
  if (fields.evidenceKind === "") errors.evidenceKind = "Choose what proves the quote.";
  const reference = fields.evidenceReference.trim();
  if (reference === "") errors.evidenceReference = "Say where the supplier's quote is.";
  else if (reference.length > TEXT_MAX) {
    errors.evidenceReference = `Use at most ${TEXT_MAX} characters.`;
  }
  return errors;
}

function QuoteFields({
  fields,
  errors,
  disabled,
  onChange,
}: Readonly<{
  fields: QuoteFieldsState;
  errors: QuoteErrors;
  disabled: boolean;
  onChange: (next: QuoteFieldsState) => void;
}>) {
  const set = (key: keyof QuoteFieldsState) => (value: string) =>
    onChange({ ...fields, [key]: value });
  return (
    <>
      <TextField
        id="quote-amount"
        label="Supplier's amount (rupees)"
        hint="The price the supplier quoted, for example 42000 or 42000.50. This is the amount the customer is asked to pay."
        value={fields.amount}
        inputMode="decimal"
        required
        disabled={disabled}
        error={errors.amount}
        onChange={set("amount")}
      />
      <TextField
        id="quote-description"
        label="One line the customer sees"
        value={fields.description}
        maxLength={TEXT_MAX}
        required
        disabled={disabled}
        error={errors.description}
        onChange={set("description")}
      />
      <TextAreaField
        id="quote-terms"
        label="Terms the customer reads"
        hint="What the price covers and its conditions. The customer sees these words with the amount."
        value={fields.terms}
        rows={3}
        maxLength={TERMS_MAX}
        required
        disabled={disabled}
        error={errors.terms}
        onChange={set("terms")}
      />
      <SelectField
        id="quote-evidence-kind"
        label="What proves the quote"
        placeholder="Choose"
        options={supplierEvidenceOptions}
        value={fields.evidenceKind}
        required
        disabled={disabled}
        error={errors.evidenceKind}
        onChange={set("evidenceKind")}
      />
      <TextField
        id="quote-evidence-reference"
        label="Where the supplier's quote is"
        hint="Staff only. For example an e-mail subject or a portal reference. The customer never sees it."
        value={fields.evidenceReference}
        maxLength={TEXT_MAX}
        required
        disabled={disabled}
        error={errors.evidenceReference}
        onChange={set("evidenceReference")}
      />
      <SelectField
        id="quote-validity"
        label="How long the quote stays valid"
        options={quoteValidityOptions}
        value={fields.validityDays}
        disabled={disabled}
        onChange={set("validityDays")}
      />
    </>
  );
}

function Actions({
  terminal,
  inFlight,
  confirm,
  retry,
  close,
  tone,
}: Readonly<{
  terminal: boolean;
  inFlight: boolean;
  confirm: string;
  retry: boolean;
  close: () => void;
  tone?: "danger";
}>) {
  return (
    <div className="flex flex-wrap gap-2">
      {terminal ? null : (
        <Button
          type="submit"
          loading={inFlight}
          variant={tone === "danger" ? "destructive" : "default"}
        >
          {inFlight ? "Working…" : retry ? "Try again" : confirm}
        </Button>
      )}
      <Button variant="secondary" onClick={close} disabled={inFlight}>
        {terminal ? "Close" : "Cancel"}
      </Button>
    </div>
  );
}

function FailureWithHint({
  failure,
  paymentId,
}: Readonly<{ failure: PaymentFailure; paymentId?: string | null | undefined }>) {
  // The links open the Payments area, which is on only where a real provider runs (CHT-1981): the offering gives no address otherwise.
  const areaHref = useAreaHref();
  // No payment id, no link: an absent id never turns into an address.
  const paymentHref = paymentId ? areaHref("payments", deepLinks.payment(paymentId)) : null;
  let hint: ReactNode = null;
  if (failure.quoteCode === "payment_request.settlement_possible" && paymentHref !== null) {
    hint = (
      <Link className={linkClass} to={paymentHref}>
        Open the payment to reconcile it.
      </Link>
    );
  } else if (failure.quoteCode === "payment_quote.open_payment_exists" && paymentHref !== null) {
    hint = (
      <Link className={linkClass} to={paymentHref}>
        Open the payment.
      </Link>
    );
  } else if (!failure.retryable) {
    hint = "Close this window and read the quote again.";
  }
  return <PaymentFailureNotice failure={failure} hint={hint} />;
}

function validUntilOf(now: Date, days: string): string {
  return new Date(now.getTime() + Number(days) * 24 * 3_600_000).toISOString();
}

// --- record -------------------------------------------------------------------------------------------------------------------

export function RecordQuoteDialog({
  requestId,
  requestVersion,
  onDone,
  onClose,
}: Readonly<{
  requestId: string;
  /** The version of the concierge request the staff user read. */
  requestVersion: number;
  onDone: () => void;
  onClose: () => void;
}>) {
  const { quotes, now } = usePaymentsRuntime();
  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState<QuoteErrors>({});
  const [attempt, setAttempt] = useState(0);
  const form = useRef<HTMLFormElement>(null);
  const shell = useDialogRun();
  useEffect(() => {
    if (attempt > 0) focusFirstInvalid(form.current);
  }, [attempt]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const found = validateFields(fields);
    setErrors(found);
    const amountMinor = parseRupeesToMinor(fields.amount);
    if (Object.keys(found).length > 0 || amountMinor === null) {
      setAttempt((count) => count + 1);
      return;
    }
    void shell.run(
      () => ({
        expectedVersion: requestVersion,
        amountMinor,
        currency: "INR" as const,
        description: fields.description.trim(),
        customerTerms: fields.terms.trim(),
        evidence: {
          kind: fields.evidenceKind as "SUPPLIER_EMAIL",
          reference: fields.evidenceReference.trim(),
        },
        validUntil: validUntilOf(now(), fields.validityDays),
      }),
      (key, body) => quotes.record(requestId, body, { idempotencyKey: key }),
      onDone,
    );
  }

  const close = shell.guardedClose(onClose);
  return (
    <Dialog
      title="Record a supplier quote"
      description="The quote is approved when you record it. The customer is asked to pay the amount you type, and nobody can change it afterwards: a new amount is a new quote."
      onClose={close}
    >
      <form
        ref={form}
        noValidate
        onSubmit={submit}
        className="grid gap-4"
        aria-label="Record a supplier quote"
      >
        {shell.terminal ? null : (
          <QuoteFields
            fields={fields}
            errors={errors}
            disabled={shell.locked}
            onChange={setFields}
          />
        )}
        {shell.failure === null ? null : <FailureWithHint failure={shell.failure} />}
        <Actions
          terminal={shell.terminal}
          inFlight={shell.inFlight}
          confirm="Record and approve the quote"
          retry={shell.failure?.retryable === true}
          close={close}
        />
      </form>
    </Dialog>
  );
}

// --- supersede ----------------------------------------------------------------------------------------------------------------

export function SupersedeQuoteDialog({
  quote,
  onDone,
  onClose,
}: Readonly<{
  quote: StaffPaymentQuoteViewResponse;
  onDone: (result: Readonly<{ issued: boolean; cancelledPayment: boolean }>) => void;
  onClose: () => void;
}>) {
  const { quotes, now } = usePaymentsRuntime();
  const [fields, setFields] = useState(EMPTY);
  const [reason, setReason] = useState("");
  const [issue, setIssue] = useState(true);
  const [errors, setErrors] = useState<QuoteErrors & { reason?: string }>({});
  const [attempt, setAttempt] = useState(0);
  const form = useRef<HTMLFormElement>(null);
  const shell = useDialogRun();
  useEffect(() => {
    if (attempt > 0) focusFirstInvalid(form.current);
  }, [attempt]);

  const payment = quote.paymentRequest;
  const paid =
    payment !== null && (payment.status === "PAID" || payment.status === "PARTIALLY_REFUNDED");

  function submit(event: FormEvent) {
    event.preventDefault();
    const found: QuoteErrors & { reason?: string } = { ...validateFields(fields) };
    const trimmed = reason.trim();
    if (trimmed === "") found.reason = "Write a reason.";
    else if (trimmed.length > REASON_MAX) found.reason = `Use at most ${REASON_MAX} characters.`;
    setErrors(found);
    const amountMinor = parseRupeesToMinor(fields.amount);
    if (Object.keys(found).length > 0 || amountMinor === null) {
      setAttempt((count) => count + 1);
      return;
    }
    void shell.run(
      () => ({
        expectedVersion: quote.version,
        reason: trimmed,
        replacement: {
          amountMinor,
          currency: "INR" as const,
          description: fields.description.trim(),
          customerTerms: fields.terms.trim(),
          evidence: {
            kind: fields.evidenceKind as "SUPPLIER_EMAIL",
            reference: fields.evidenceReference.trim(),
          },
          validUntil: validUntilOf(now(), fields.validityDays),
        },
        issuePaymentRequest: issue,
      }),
      (key, body) => quotes.supersede(quote.id, body, { idempotencyKey: key }),
      (result) =>
        onDone({
          issued: result.paymentRequestId !== null,
          cancelledPayment: result.cancelledPaymentRequestId !== null,
        }),
    );
  }

  const close = shell.guardedClose(onClose);
  return (
    <Dialog
      title="Replace this quote"
      description={`The old quote (${formatMinor(quote.amountMinor, quote.currency)}) is withdrawn and a new quote takes its place. The old quote can never be paid.`}
      onClose={close}
    >
      <form
        ref={form}
        noValidate
        onSubmit={submit}
        className="grid gap-4"
        aria-label="Replace this quote"
      >
        {paid ? (
          <Notice tone="warning" role="alert">
            <p>
              The customer already paid this quote. Refund the payment first, then replace the
              quote.
            </p>
          </Notice>
        ) : null}
        {shell.terminal ? null : (
          <>
            {payment !== null && !paid ? (
              <Notice tone="warning" role="note">
                <p>
                  The open payment request (
                  {payment.status === "READY" ? "ready to pay" : "waiting for payment"}) will be
                  cancelled first.{" "}
                  {payment.status === "PENDING_PAYMENT"
                    ? "Lafayette asks the payment provider before it cancels, and refuses if the customer may have paid."
                    : null}
                </p>
              </Notice>
            ) : null}
            <QuoteFields
              fields={fields}
              errors={errors}
              disabled={shell.locked || paid}
              onChange={setFields}
            />
            <TextAreaField
              id="quote-supersede-reason"
              label="Why the quote changes"
              hint="Staff only. It goes into the history. The customer never sees it."
              value={reason}
              rows={3}
              maxLength={REASON_MAX}
              required
              disabled={shell.locked || paid}
              error={errors.reason}
              onChange={setReason}
            />
            <CheckboxField
              id="quote-supersede-issue"
              label="Ask the customer to pay the new quote now"
              hint="Creates the new payment request from the new quote. The amount is the new quote's, copied by the server."
              checked={issue}
              disabled={shell.locked || paid}
              onChange={setIssue}
            />
          </>
        )}
        {shell.failure === null ? null : (
          <FailureWithHint failure={shell.failure} paymentId={payment?.id} />
        )}
        <Actions
          terminal={shell.terminal || paid}
          inFlight={shell.inFlight}
          confirm="Replace the quote"
          retry={shell.failure?.retryable === true}
          close={close}
          tone="danger"
        />
      </form>
    </Dialog>
  );
}

// --- issue --------------------------------------------------------------------------------------------------------------------

export function IssuePaymentDialog({
  quote,
  onDone,
  onClose,
}: Readonly<{
  quote: StaffPaymentQuoteViewResponse;
  onDone: () => void;
  onClose: () => void;
}>) {
  const { quotes } = usePaymentsRuntime();
  const shell = useDialogRun();
  function submit(event: FormEvent) {
    event.preventDefault();
    void shell.run(
      () => ({}),
      (key) => quotes.issuePayment(quote.id, {}, { idempotencyKey: key }),
      onDone,
    );
  }
  const close = shell.guardedClose(onClose);
  return (
    <Dialog
      title="Ask the customer to pay"
      description="This creates a payment request from the approved quote. You do not type an amount: the server copies it from the quote."
      onClose={close}
    >
      <form
        noValidate
        onSubmit={submit}
        className="grid gap-4"
        aria-label="Ask the customer to pay"
      >
        <p className="m-0 text-lg">
          The customer will be asked to pay{" "}
          <strong>
            <Amount>{formatMinor(quote.amountMinor, quote.currency)}</Amount>
          </strong>
          .
        </p>
        <p className={hintClass}>
          The customer reads: {quote.customerTerms ?? "no terms were recorded"}
        </p>
        {shell.failure === null ? null : <FailureWithHint failure={shell.failure} />}
        <Actions
          terminal={shell.terminal}
          inFlight={shell.inFlight}
          confirm="Create the payment request"
          retry={shell.failure?.retryable === true}
          close={close}
        />
      </form>
    </Dialog>
  );
}
